import useLastSavedLabel from 'hooks/useLastSavedLabel';
import { useTranslation } from 'react-i18next';
import Button from 'shell/components/Button';
import Stack from 'shell/components/Stack';
import Text from 'shell/components/Text';
import { ASSIGNMENT_EDITOR_ACTION } from 'utils/assignmentEditorConstants';
import { ViewportDevice } from 'utils/constants';

const LastSavedStatus = ({ lastSavedAt, compact }) => {
    const { t } = useTranslation('assignmentSettings');
    const savedLabel = useLastSavedLabel(lastSavedAt, t);

    if (!lastSavedAt) return null;

    return (
        <Text
            aria-live={'polite'}
            color={'light'}
            sx={
                compact
                    ? { marginLeft: 'auto', textAlign: 'right' }
                    : { flex: '1 1 auto', minWidth: '10rem', textAlign: 'right' }
            }
        >
            {savedLabel}
        </Text>
    );
};

const HeaderActionControls = ({
    busy,
    isPersisted,
    publicationAction,
    autosaveStatusContent,
    onClickCancel,
    onClickDelete,
    onAction,
}) => {
    const { t } = useTranslation('assignmentSettings');

    return (
        <>
            {!isPersisted ? (
                <Button variant={'borderless'} color={'neutral'} onClick={onClickCancel} disabled={busy}>
                    {t('common:controls.cancel', 'Cancel')}
                </Button>
            ) : (
                <Button
                    variant={'borderless'}
                    color={'neutral'}
                    sx={(theme) => ({ color: theme.palette.ensightful.error[700] })}
                    disabled={busy}
                    onClick={onClickDelete}
                >
                    {t('common:controls.delete', 'Delete')}
                </Button>
            )}
            <Stack
                direction={'row'}
                alignItems={'center'}
                justifyContent={'center'}
                useFlexGap
                spacing={1}
                sx={{ width: '4rem', minHeight: '2.25rem', flexShrink: 0, whiteSpace: 'nowrap' }}
            >
                {autosaveStatusContent}
            </Stack>
            {publicationAction && (
                <Button
                    variant={publicationAction === ASSIGNMENT_EDITOR_ACTION.PUBLISH ? 'primary' : 'secondary'}
                    loading={busy && publicationAction === ASSIGNMENT_EDITOR_ACTION.PUBLISH}
                    disabled={busy}
                    onClick={() => onAction(publicationAction)}
                >
                    {t(
                        publicationAction === ASSIGNMENT_EDITOR_ACTION.PUBLISH
                            ? 'common:controls.publish'
                            : 'common:controls.unpublish',
                        publicationAction === ASSIGNMENT_EDITOR_ACTION.PUBLISH ? 'Publish' : 'Unpublish'
                    )}
                </Button>
            )}
        </>
    );
};

const CompactActionsRow = ({ children }) => (
    <Stack
        direction={'row'}
        alignItems={'center'}
        justifyContent={'space-between'}
        useFlexGap
        flexWrap={'wrap'}
        spacing={4}
        sx={{ width: '100%', rowGap: 2 }}
    >
        {children}
    </Stack>
);

const RegularActionsRow = ({ children }) => (
    <Stack
        direction={'row'}
        alignItems={'center'}
        justifyContent={'flex-end'}
        useFlexGap
        flexWrap={'wrap'}
        spacing={4}
        sx={{ flexGrow: 1 }}
    >
        {children}
    </Stack>
);

const AssignmentEditorHeaderActions = ({
    device,
    busy,
    isPersisted,
    publicationAction,
    autosaveStatusContent,
    lastSavedAt,
    onClickCancel,
    onClickDelete,
    onAction,
}) => {
    const isCompactLayout = device === ViewportDevice.MOBILE;

    if (isCompactLayout) {
        return (
            <>
                <LastSavedStatus lastSavedAt={lastSavedAt} compact />
                <CompactActionsRow>
                    <HeaderActionControls
                        busy={busy}
                        isPersisted={isPersisted}
                        publicationAction={publicationAction}
                        autosaveStatusContent={autosaveStatusContent}
                        onClickCancel={onClickCancel}
                        onClickDelete={onClickDelete}
                        onAction={onAction}
                    />
                </CompactActionsRow>
            </>
        );
    }

    return (
        <RegularActionsRow>
            <LastSavedStatus lastSavedAt={lastSavedAt} />
            <HeaderActionControls
                busy={busy}
                isPersisted={isPersisted}
                publicationAction={publicationAction}
                autosaveStatusContent={autosaveStatusContent}
                onClickCancel={onClickCancel}
                onClickDelete={onClickDelete}
                onAction={onAction}
            />
        </RegularActionsRow>
    );
};

export default AssignmentEditorHeaderActions;
